
import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Clock, Zap, AlertCircle, CheckCircle, ArrowRight, Star } from 'lucide-react';

const TimeEffortComparisonSection = () => {
  const { isMobile } = useBreakpoint();

  const withoutPackage = [
    { task: "Creating a landing page", time: "10-15 hours" },
    { task: "Scripting and producing a video", time: "20-30 hours" },
    { task: "Designing templates", time: "15-20 hours" }
  ];

  const withPackage = [
    { task: "Upload the landing page", time: "30 minutes" },
    { task: "Customize with your info", time: "15 minutes" }
  ];

  return (
    <SectionLayout 
      backgroundColor="bg-gradient-to-br from-white via-[#FEE6D8]/5 to-white" 
      paddingY="py-16 md:py-20 lg:py-24"
      containerWidth="wide"
      backgroundPattern="subtle-grid"
      patternColor="#003a70"
      patternOpacity={2}
    >
      <div className="text-center space-y-12 md:space-y-16">
        
        {/* Section Header */}
        <div className="space-y-6 animate-fade-in">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#003a70]/10 to-[#FEE6D8]/30 rounded-full shadow-lg border-2 border-[#003a70]/20">
            <Zap className="text-[#003a70]" size={isMobile ? 24 : 28} />
            <span className="text-[#003a70] font-bold text-lg md:text-xl">Game Changer</span>
          </div>
          
          <h2 className={cn(
            "font-bold text-[#003a70] leading-tight max-w-4xl mx-auto",
            isMobile ? "text-2xl md:text-3xl" : "text-3xl lg:text-4xl xl:text-5xl"
          )}>
            Here's Why This Changes
            <br />
            <span className="bg-gradient-to-r from-green-600 to-[#003a70] bg-clip-text text-transparent">
              Everything
            </span>
          </h2>
        </div>

        {/* Comparison Grid */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 max-w-7xl mx-auto">
          
          {/* Without Package */}
          <div className="space-y-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="bg-white rounded-3xl p-8 md:p-10 border-2 border-red-200/50 shadow-2xl relative overflow-hidden">
              {/* Warning gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-red-50/60 via-transparent to-red-100/40 pointer-events-none"></div>
              
              {/* Warning accent bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-red-400 to-red-500"></div>
              
              <div className="relative z-10">
                {/* Header */}
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg">
                      <AlertCircle className="text-white" size={28} />
                    </div>
                  </div>
                  <h3 className={cn(
                    "font-bold text-red-600 mb-2",
                    isMobile ? "text-xl md:text-2xl" : "text-2xl lg:text-3xl"
                  )}>
                    Without This Package
                  </h3>
                  <p className="text-red-500 font-semibold text-lg">
                    The Old Way (Painful & Slow)
                  </p>
                </div>

                {/* Tasks List */}
                <div className="space-y-4">
                  {withoutPackage.map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-4 bg-red-50/80 rounded-xl border border-red-200/40"
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="text-red-500 flex-shrink-0" size={20} />
                        <span className={cn(
                          "text-red-700 font-medium",
                          isMobile ? "text-sm" : "text-base"
                        )}>
                          {item.task}
                        </span>
                      </div>
                      <span className={cn(
                        "text-red-600 font-bold",
                        isMobile ? "text-sm" : "text-base"
                      )}>
                        {item.time}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Total Time */}
                <div className="mt-8 p-6 bg-gradient-to-r from-red-500/10 to-red-600/10 rounded-2xl border-2 border-red-300/50">
                  <div className="text-center">
                    <p className="text-red-600 font-semibold mb-2 text-lg">
                      Total Time Investment:
                    </p>
                    <p className={cn(
                      "text-red-700 font-bold",
                      isMobile ? "text-2xl" : "text-3xl lg:text-4xl"
                    )}>
                      45-65 Hours!
                    </p>
                    <p className="text-red-500 font-medium mt-2">
                      That's over a week of full-time work!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* With Package */}
          <div className="space-y-6 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <div className="bg-white rounded-3xl p-8 md:p-10 border-2 border-green-200/50 shadow-2xl relative overflow-hidden">
              {/* Success gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-green-50/60 via-transparent to-green-100/40 pointer-events-none"></div>
              
              {/* Success accent bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-green-400 to-green-500"></div>
              
              <div className="relative z-10">
                {/* Header */}
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                      <CheckCircle className="text-white" size={28} />
                    </div>
                  </div>
                  <h3 className={cn(
                    "font-bold text-green-600 mb-2",
                    isMobile ? "text-xl md:text-2xl" : "text-2xl lg:text-3xl"
                  )}>
                    With This Package
                  </h3>
                  <p className="text-green-500 font-semibold text-lg">
                    The Smart Way (Fast & Easy)
                  </p>
                </div>

                {/* Tasks List */}
                <div className="space-y-4">
                  {withPackage.map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-4 bg-green-50/80 rounded-xl border border-green-200/40"
                    >
                      <div className="flex items-center gap-3">
                        <Zap className="text-green-500 flex-shrink-0" size={20} />
                        <span className={cn(
                          "text-green-700 font-medium",
                          isMobile ? "text-sm" : "text-base"
                        )}>
                          {item.task}
                        </span>
                      </div>
                      <span className={cn(
                        "text-green-600 font-bold",
                        isMobile ? "text-sm" : "text-base"
                      )}>
                        {item.time}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Total Time */}
                <div className="mt-8 p-6 bg-gradient-to-r from-green-500/10 to-green-600/10 rounded-2xl border-2 border-green-300/50">
                  <div className="text-center">
                    <p className="text-green-600 font-semibold mb-2 text-lg">
                      Total Time Investment:
                    </p>
                    <p className={cn(
                      "text-green-700 font-bold",
                      isMobile ? "text-2xl" : "text-3xl lg:text-4xl"
                    )}>
                      45 Minutes!
                    </p>
                    <p className="text-green-500 font-medium mt-2">
                      And you're ready to sell!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Impact Statement */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <div className="bg-gradient-to-br from-[#FEE6D8]/30 via-white to-[#003a70]/5 rounded-3xl p-8 md:p-12 border-2 border-[#FEE6D8]/50 shadow-2xl max-w-5xl mx-auto relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-4 right-4 opacity-10">
              <Star className="text-[#003a70]" size={48} />
            </div>
            <div className="absolute bottom-4 left-4 opacity-10">
              <Zap className="text-[#003a70]" size={48} />
            </div>
            
            <div className="relative z-10 text-center">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="flex items-center gap-2 px-4 py-2 bg-red-100 rounded-full">
                  <span className="text-red-600 font-bold text-2xl">65 hours</span>
                </div>
                <ArrowRight className="text-[#003a70]" size={32} />
                <div className="flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full">
                  <span className="text-green-600 font-bold text-2xl">45 minutes</span>
                </div>
              </div>
              
              <h3 className={cn(
                "font-bold text-[#003a70] mb-6 leading-tight",
                isMobile ? "text-xl md:text-2xl" : "text-2xl lg:text-3xl"
              )}>
                That's a <span className="text-green-600">98.8% time savings!</span>
              </h3>
              
              <p className={cn(
                "text-[#003a70]/90 leading-relaxed max-w-3xl mx-auto",
                isMobile ? "text-lg" : "text-xl lg:text-2xl"
              )}>
                Instead of spending weeks creating everything from scratch, you could be selling 
                <span className="font-bold text-[#003a70]"> in less than an hour.</span>
              </p>
              
              <div className="mt-8 p-6 bg-gradient-to-r from-[#003a70]/5 to-[#FEE6D8]/20 rounded-xl border border-[#FEE6D8]/40">
                <p className={cn(
                  "text-[#003a70] font-bold",
                  isMobile ? "text-lg" : "text-xl"
                )}>
                  What would you rather do with those extra 64+ hours?
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </SectionLayout>
  );
};

export default TimeEffortComparisonSection;
